'use strict';

define('vb/action/builtin/navigateAction',['vb/action/action'], (Action) => {
  /** @typedef {import('vb/action/action').Outcome} Outcome */

  /**
   * Represents a generic navigation action that can navigate to pages, flows, or App UIs.
   *
   * @extends Action
   * @category Action
   */
  class NavigateAction extends Action {
    /**
     * A generic way to navigate to page, flow or App UI.
     *
     * @param {Object} parameters
     * @param {String} parameters.page - The path to the destination page. The path is absolute starting
     * at the application or relative to the current page. When use in combination with flow or App UI,
     * the path cannot be absolute and it navigates to the page relative to the flow or App UI.
     * @param {String} [parameters.flow] - The id of the destination flow. Change the content of the flow displayed
     * in the current page. When used in combination with a page property, navigates to the page in that flow.
     * @param {String} [parameters.application] - the id of the destination App UI. Change which App UI
     * is displayed in the host application. When used in combination with a page and flow property, navigates
     * to the page in that App UI.
     * @param {'self'|'parent'} [parameters.target="self"] - The target of the destination for flow navigation.
     * This is only valid with the flow to change the content of the parent flow instead of the nested flow.
     * @param {Object} [parameters.params] - A map of URL parameters. Same as in the navigateToPage
     * action
     * @param {'push'|'replace'|'skip'} [parameters.history="push"] - Effect on the browser history. If the value
     * is 'replace', the current browser history entry is replaced, meaning that back button will not go back to it.
     * If the value is 'skip', the URL is left untouched.
     * @returns {Promise<Outcome>}
     * Returns the outcome 'success' if there was no error during navigation. If navigation
     * completed, returns the payload { navigated: true }.
     * Returns the outcome 'failure' with the error in the payload.
     */
    perform(parameters) {
      return this.helpers.navigate(parameters)
        .then((result) => Action.createSuccessOutcome(result))
        .catch((e) => Action.createFailureOutcome('Error during navigateAction', e));
    }
  }

  return NavigateAction;
});

